import React from 'react';
import { Check, ChevronRight } from 'lucide-react';

// Brand monogram in a clean white-bordered tile (used on store cards)
export function BrandMono({ letter, size = 48 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        color: '#000',
        fontFamily: 'Anton, Space Grotesk, sans-serif',
        fontWeight: 400,
        fontSize: size * 0.55,
        letterSpacing: '-0.02em',
        boxShadow: '0 0 0 1px rgba(245,245,245,0.15), 0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      {letter}
    </div>
  );
}

// Cyber chip illustration (CSS only) for jammers
export function CyberChip({ size = 64 }) {
  const pin = (i, side) => {
    const style = {
      position: 'absolute',
      width: 6,
      height: 2,
      background: 'rgba(245,245,245,0.65)',
    };
    const offset = (i + 1) * (size / 6);
    if (side === 'left')   return { ...style, left: -6, top: offset };
    if (side === 'right')  return { ...style, right: -6, top: offset };
    if (side === 'top')    return { ...style, top: -6, left: offset, width: 2, height: 6 };
    return { ...style, bottom: -6, left: offset, width: 2, height: 6 };
  };
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #050505 100%)',
        borderRadius: 8,
        border: '1px solid rgba(245,245,245,0.18)',
        boxShadow: '0 0 0 1px rgba(245,245,245,0.06), 0 8px 24px rgba(0,0,0,0.6), inset 0 0 20px rgba(245,245,245,0.04)',
      }}
    >
      {[0, 1, 2, 3].map((i) => <div key={`l${i}`} style={pin(i, 'left')} />)}
      {[0, 1, 2, 3].map((i) => <div key={`r${i}`} style={pin(i, 'right')} />)}
      {[0, 1, 2, 3].map((i) => <div key={`t${i}`} style={pin(i, 'top')} />)}
      {[0, 1, 2, 3].map((i) => <div key={`b${i}`} style={pin(i, 'bottom')} />)}
      <div
        style={{
          position: 'absolute',
          inset: 8,
          border: '1px solid rgba(245,245,245,0.18)',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: size * 0.18,
          color: 'rgba(245,245,245,0.7)',
          letterSpacing: '0.15em',
          background: 'radial-gradient(circle at 30% 30%, rgba(245,245,245,0.06), transparent 60%)',
        }}
      >
        IC
      </div>
    </div>
  );
}

// Reusable feature ticks list
export function TickList({ items }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            color: 'rgba(245,245,245,0.78)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <Check size={14} strokeWidth={2.2} style={{ marginTop: 3, color: '#f5f5f5', flexShrink: 0 }} />
          <span>{it}</span>
        </div>
      ))}
    </div>
  );
}

// CTA pill card used at the bottom of each section
export function ContactPromptCard({ message }) {
  const goContact = () => {
    const el = document.getElementById('contact');
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  };
  return (
    <div
      onClick={goContact}
      style={{
        marginTop: 36,
        padding: '24px 28px',
        border: '1px dashed rgba(245,245,245,0.18)',
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        cursor: 'pointer',
        transition: 'border-color 220ms ease, background 220ms ease, padding 220ms ease',
      }}
      className="contact-prompt"
    >
      <div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.35em',
            color: 'rgba(245,245,245,0.5)',
            textTransform: 'uppercase',
          }}
        >
          / Need something else?
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: 18,
            color: '#f5f5f5',
          }}
        >
          {message}
        </div>
      </div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          borderRadius: 999,
          background: '#f5f5f5',
          color: '#000',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        Contact me <ChevronRight size={14} />
      </div>
    </div>
  );
}
